import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from "react";
import { useCandidateSelection } from "../../App/State/Hooks/useCandidateSelection";
import { ICandidate } from "../../App/Typings/Candidate";

interface IManageCandidateContext {
  getters: {
    actionCandidate?: ICandidate;
    selectedCandidates: ICandidate[];
    selectedCandidateIds: string[];
    selectedCount: number;
    selectAll: boolean;
    excludedCandidateIds: string[];
    disableSelectAll: boolean;
    isAddCandidateOpen: boolean;
    isAddToGroupOpen: boolean;
    textCandidates: ICandidate[];
    emailCandidates: ICandidate[];
    hasValidMessageCandidates: boolean;
    isCandidateSelected: (candidateId: string) => boolean;
    currentPageSelectedIds: string[];
  };
  setters: {
    setActionCandidate: Dispatch<SetStateAction<ICandidate | undefined>>;
    setSelectedCandidates: (candidates: ICandidate[]) => void;
    setAddCandidateOpen: Dispatch<SetStateAction<boolean>>;
    setAddToGroupOpen: Dispatch<SetStateAction<boolean>>;
    setTotalAvailableCount: Dispatch<SetStateAction<number>>;
  };
  handlers: {
    handleSelectRow: (row: ICandidate, select: boolean) => void;
    handleSelectAllClick: (selected: boolean, currentPageCandidates?: ICandidate[]) => void;
    handleClearSelected: () => void;
  };
}

const defaultManageCandidatesContext = {
  getters: {
    actionCandidate: undefined,
    selectedCandidates: [],
    selectedCandidateIds: [],
    selectedCount: 0,
    selectAll: false,
    excludedCandidateIds: [],
    disableSelectAll: false,
    isAddCandidateOpen: false,
    isAddToGroupOpen: false,
    textCandidates: [],
    emailCandidates: [],
    hasValidMessageCandidates: false,
    isCandidateSelected: (_: string) => false,
    currentPageSelectedIds: [],
  },
  setters: {
    setActionCandidate: () => {},
    setSelectedCandidates: () => {},
    setAddCandidateOpen: () => {},
    setAddToGroupOpen: () => {},
    setTotalAvailableCount: () => {},
  },
  handlers: {
    handleSelectRow: () => {},
    handleSelectAllClick: () => {},
    handleClearSelected: () => {},
  },
};

const ManageCandidateSelectionContext = createContext<IManageCandidateContext>(
  defaultManageCandidatesContext,
);

export const CandidateSelectionProvider = ({ children }: { children: ReactNode }) => {
  const [actionCandidate, setActionCandidate] = useState<ICandidate | undefined>();
  const [isAddCandidateOpen, setAddCandidateOpen] = useState<boolean>(false);
  const [isAddToGroupOpen, setAddToGroupOpen] = useState<boolean>(false);
  const [totalAvailableCount, setTotalAvailableCount] = useState<number>(0);

  const {
    getters: {
      selectedCandidates,
      selectedCandidateIds,
      selectedCount,
      selectAll,
      excludedCandidateIds,
      disableSelectAll,
      isCandidateSelected,
      currentPageSelectedIds,
    },
    setters: { setSelectedCandidates },
    handlers: { handleSelectRow, handleSelectAllClick, handleClearSelected },
  } = useCandidateSelection({
    totalAvailableCount,
  });

  const textCandidates = useMemo(
    () =>
      selectedCandidates.filter(
        (c) => c.phoneNumbers && c.phoneNumbers.find((phone) => phone.isValid) !== undefined,
      ),
    [selectedCandidates],
  );
  const emailCandidates = useMemo(
    () => selectedCandidates.filter((c) => c.emailAddresses && c.emailAddresses.length > 0),
    [selectedCandidates],
  );
  const hasValidMessageCandidates = useMemo(
    () => textCandidates.length > 0 || emailCandidates.length > 0,
    [textCandidates, emailCandidates],
  );

  return (
    <ManageCandidateSelectionContext.Provider
      value={{
        getters: {
          actionCandidate,
          selectedCandidates,
          selectedCandidateIds,
          selectedCount,
          selectAll,
          excludedCandidateIds,
          disableSelectAll,
          isAddCandidateOpen,
          isAddToGroupOpen,
          textCandidates,
          emailCandidates,
          hasValidMessageCandidates,
          isCandidateSelected,
          currentPageSelectedIds,
        },
        setters: {
          setActionCandidate,
          setSelectedCandidates,
          setAddCandidateOpen,
          setAddToGroupOpen,
          setTotalAvailableCount,
        },
        handlers: {
          handleSelectRow,
          handleSelectAllClick,
          handleClearSelected,
        },
      }}
    >
      {children}
    </ManageCandidateSelectionContext.Provider>
  );
};

export const useManageCandidateSelectionContext = () => useContext(ManageCandidateSelectionContext);
